import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { FacebookShareButton, TwitterShareButton } from 'react-share';

import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import EditIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import TwitterIcon from '@mui/icons-material/Twitter';
import Typography from '@mui/material/Typography';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { isAddressesEqual } from '@utils/tokens.utils';

import Button from '@components/common/Button';
import CopyClipboard from '@components/common/CopyClipboard';
import ModalWrapper from '@components/common/Modal/ModalWrapper';

import { APP_URL } from '@constants/common/common.constants';
import { Variant } from '@constants/common/ui.constants';

import { VaultInformationProps } from '@app-types/common/modal.types';

import EditForm from './EditForm';
import Row from './Row';
import useStyles from './styles.module';

const DESCRIPTION =
  'The owner of this strategy will receive 1% of the total take profit on each harvest from all participants';

const VaultInformation: FC<VaultInformationProps> = ({ vault }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { classes } = useStyles();
  const { account } = useAuth();

  const shareUrl = useMemo(() => `${APP_URL}/vaults/${vault.id}`, [vault]);

  const shareTitle = useMemo(
    () =>
      `I just joined ${vault.title} investment ` +
      'strategy on ' +
      `#dexVAULTS thanks to @DexFinance ${vault.publisherName ? `and ${vault.publisherName}` : ''} ` +
      'Join with us:',
    [vault],
  );

  const handleOpenForm = useCallback(() => {
    setIsFormOpen((prev) => !prev);
  }, []);

  return (
    <ModalWrapper title="Vault Information">
      <div className={classes.box}>
        <Typography variant="body1" align="center">
          {DESCRIPTION}
        </Typography>

        {!isFormOpen && (
          <div className={classes.content}>
            <Row title="Vault name:" value={vault.title} />
            <Row title="Publisher name:" value={vault.publisherName} />
            <Row
              title="Description:"
              value={vault.description}
              className={classes.desc}
            />
            {isAddressesEqual(vault.creator, account) && (
              <Button
                onClick={handleOpenForm}
                variant={Variant.Secondary}
                size="small"
                iconRight={<EditIcon />}
              >
                Edit
              </Button>
            )}
          </div>
        )}

        {isFormOpen && <EditForm vault={vault} onOpenForm={handleOpenForm} />}

        <div className={classes.content}>
          <Row
            title="Share link:"
            value={
              <CopyClipboard value={shareUrl}>
                <Typography variant="body1" className={classes.linkString}>
                  {shareUrl}
                </Typography>
              </CopyClipboard>
            }
          />
          <Row title="Custom ID:" value={vault.id} />
        </div>

        <div className={classes.sharedIcons}>
          <FacebookShareButton url={shareUrl} className={classes.btn}>
            <FacebookRoundedIcon />
          </FacebookShareButton>
          <TwitterShareButton
            url={shareUrl}
            className={classes.btn}
            title={shareTitle}
          >
            <TwitterIcon />
          </TwitterShareButton>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default memo(VaultInformation);
